import React, { useEffect, useState, useRef } from 'react';
import { usePublicationDetail } from '../../../hooks/usePublicationDetail';
import { usePublications } from '../../../hooks/usePublications';
import PublicationMedia, { LinkifiedText } from './PublicationMedia';
import LikeShareBar from './LikeShareBar';
import './PublicationModal.css';
import { logger } from '../../../utils/logger';

interface PublicationModalProps {
  publicationId: string;
  tenantId: string;
  customerId?: string;
  onClose: () => void;
}

const PublicationModal: React.FC<PublicationModalProps> = ({
  publicationId,
  tenantId,
  customerId,
  onClose,
}) => {
  const { publication, loading, error } = usePublicationDetail(publicationId, tenantId);
  const { trackView } = usePublications({}, tenantId);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const viewTrackedRef = useRef(false);

  useEffect(() => {
    // Tracker la vue UNE SEULE FOIS à l'ouverture (useRef pour éviter les doubles rendus React)
    if (!viewTrackedRef.current) {
      viewTrackedRef.current = true;
      trackView(publicationId, { customer_id: customerId });
    }

    // Empêcher le scroll du body
    document.body.style.overflow = 'hidden';

    // Restaurer le scroll à la fermeture
    return () => {
      document.body.style.overflow = 'unset';
    };
  }, [publicationId, customerId, trackView]);

  // Fermer avec la touche Escape
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose]);

  const getTypeIcon = (type: string) => {
    const icons: Record<string, string> = {
      ARRIVAGE: '🍅',
      PROMOTION: '🎉',
      ANTI_GASPI: '🌱',
      EVENEMENT: '🎪',
      INFO: 'ℹ️',
    };
    return icons[type] || '📢';
  };

  const getTypeLabel = (type: string) => {
    const labels: Record<string, string> = {
      ARRIVAGE: 'Arrivage',
      PROMOTION: 'Promotion',
      ANTI_GASPI: 'Anti-gaspi',
      EVENEMENT: 'Événement',
      INFO: 'Information',
    };
    return labels[type] || type;
  };

  const getTypeBadgeColor = (type: string) => {
    const colors: Record<string, string> = {
      ARRIVAGE: '#10b981',
      PROMOTION: '#f59e0b',
      ANTI_GASPI: '#22c55e',
      EVENEMENT: '#8b5cf6',
      INFO: '#3b82f6',
    };
    return colors[type] || '#6b7280';
  };

  const handleBackdropClick = (e: React.MouseEvent) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  const handleProductClick = (productId: string) => {
    trackView(publicationId, {
      customer_id: customerId,
      clicked_product: true,
    });
    // TODO: Ouvrir la page produit ou ajouter au panier
    logger.log('Product clicked:', productId);
  };

  const nextImage = () => {
    if (publication?.images && currentImageIndex < publication.images.length - 1) {
      setCurrentImageIndex(currentImageIndex + 1);
    }
  };

  const prevImage = () => {
    if (currentImageIndex > 0) {
      setCurrentImageIndex(currentImageIndex - 1);
    }
  };

  if (loading) {
    return (
      <div className="modal-backdrop" onClick={handleBackdropClick}>
        <div className="modal-content">
          <div className="modal-loading">
            <div className="spinner"></div>
            <p>Chargement...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error || !publication) {
    return (
      <div className="modal-backdrop" onClick={handleBackdropClick}>
        <div className="modal-content">
          <div className="modal-error">
            <div className="error-icon">⚠️</div>
            <h2>Publication introuvable</h2>
            <p>{error || 'Cette publication n\'existe pas ou n\'est plus disponible'}</p>
            <button onClick={onClose} className="btn-close-error">
              Fermer
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="modal-backdrop" onClick={handleBackdropClick}>
      <div className="modal-content">
        {/* Header avec bouton fermer */}
        <div className="modal-header">
          <div className="modal-header-left">
            <span
              className="type-badge"
              style={{ backgroundColor: getTypeBadgeColor(publication.type) }}
            >
              {getTypeIcon(publication.type)} {getTypeLabel(publication.type)}
            </span>
          </div>
          <button onClick={onClose} className="btn-close" aria-label="Fermer">
            ✕
          </button>
        </div>

        {/* Contenu scrollable */}
        <div className="modal-body">
          {/* Carousel d'images */}
          {publication.images && publication.images.length > 0 && (
            <div className="modal-images-carousel">
              <div className="carousel-main-image">
                <img
                  src={publication.images[currentImageIndex].url}
                  alt={publication.images[currentImageIndex].caption || publication.title}
                />
                {publication.images[currentImageIndex].caption && (
                  <div className="image-caption">
                    {publication.images[currentImageIndex].caption}
                  </div>
                )}
              </div>

              {publication.images.length > 1 && (
                <>
                  <button
                    className="carousel-btn carousel-prev"
                    onClick={prevImage}
                    disabled={currentImageIndex === 0}
                    aria-label="Image précédente"
                  >
                    ‹
                  </button>
                  <button
                    className="carousel-btn carousel-next"
                    onClick={nextImage}
                    disabled={currentImageIndex === publication.images.length - 1}
                    aria-label="Image suivante"
                  >
                    ›
                  </button>
                  <div className="carousel-indicators">
                    {publication.images.map((_, index) => (
                      <button
                        key={index}
                        className={`indicator ${index === currentImageIndex ? 'active' : ''}`}
                        onClick={() => setCurrentImageIndex(index)}
                        aria-label={`Aller à l'image ${index + 1}`}
                      />
                    ))}
                  </div>
                </>
              )}
            </div>
          )}

          {/* Info marchand — seulement si l'API l'a renvoyé (`/publications/:id` ne le joint pas) */}
          {publication.tenant?.name && (
            <div className="modal-merchant">
              <div className="merchant-avatar">
                {publication.tenant.name.charAt(0).toUpperCase()}
              </div>
              <div className="merchant-info">
                <h3>{publication.tenant.name}</h3>
                {publication.tenant.city && (
                  <p className="merchant-location">📍 {publication.tenant.city}</p>
                )}
              </div>
            </div>
          )}

          {/* Titre et contenu */}
          <div className="modal-main-content">
            <h1 className="modal-title">{publication.title}</h1>

            <div className="modal-meta">
              <span>👁️ {publication.views_count} vues</span>
              {publication.clicks_count > 0 && (
                <span>🛒 {publication.clicks_count} clics</span>
              )}
              <span>
                {new Date(publication.published_at || publication.created_at).toLocaleDateString('fr-FR', {
                  day: 'numeric',
                  month: 'long',
                  year: 'numeric',
                })}
              </span>
            </div>

            <div style={{ margin: '0 0 1rem' }}>
              <LikeShareBar
                publicationId={publication.id}
                title={publication.title}
                likesCount={publication.likes_count ?? 0}
                liked={publication.liked}
              />
            </div>

            {/* Vidéos / podcasts : le lecteur, avant le texte */}
            {publication.videos?.map((video) => (
              <PublicationMedia key={video.id || video.url} video={video} title={publication.title} />
            ))}

            <LinkifiedText className="modal-text" text={publication.content} />
          </div>

          {/* Produits */}
          {publication.products && publication.products.length > 0 && (
            <div className="modal-products">
              <h2 className="section-title">Produits concernés</h2>
              <div className="products-grid">
                {publication.products.map((item) => (
                  <div
                    key={item.id}
                    className="product-card"
                    onClick={() => handleProductClick(item.product_id)}
                  >
                    {item.product.image_url && (
                      <div className="product-image">
                        <img src={item.product.image_url} alt={item.product.name} />
                      </div>
                    )}
                    <div className="product-details">
                      <h3 className="product-name">{item.product.name}</h3>

                      {item.special_offer && (
                        <div className="special-offer-badge">
                          {item.special_offer}
                        </div>
                      )}

                      {item.highlight_price && item.product.prices?.[0] && (
                        <div className="product-price">
                          {(item.product.prices[0].amount_cents / 100).toFixed(2)} €
                        </div>
                      )}

                      <button className="btn-add-to-cart" onClick={(e) => {
                        e.stopPropagation();
                        handleProductClick(item.product_id);
                      }}>
                        🛒 Ajouter au panier
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Expiration */}
          {publication.expires_at && new Date(publication.expires_at) > new Date() && (
            <div className="modal-expiration">
              ⏰ Offre valable jusqu'au{' '}
              {new Date(publication.expires_at).toLocaleDateString('fr-FR', {
                day: 'numeric',
                month: 'long',
                hour: '2-digit',
                minute: '2-digit',
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default PublicationModal;
